<template>
  <k-panel :hasContentToggle="false" :title="panelTitle">
    <template #body>
      <div
        class="feedback-layout"
        :class="{ 'feedback-layout--empty': showScopeEmptyPlaceholder }"
      >
        <empty-placeholder
          v-if="showScopeEmptyPlaceholder"
          :title="scopeEmptyPlaceholder.title"
          :info="scopeEmptyPlaceholder.info"
          :fa-icon="scopeEmptyPlaceholder.faIcon"
        />
        <div
          v-else
          :class="{ 'read-only': readOnly, 'evidence-mode': feedbackType === 'evidence' }"
        >
        <notes-overview
          :filterable="false"
          :allow-adding-new-notes="false"
          :is-project-feedback="true"
          :is-learner-support="!readOnly"
          :use-custom-notes="true"
          :custom-notes="feedbackNotes"
          @custom-get-notes="getFeedback"
          @custom-edit-note="editFeedback"
          @custom-delete-note="deleteFeedback"
        >
        <template #tags="{ note }">
          <div class="tags">
            <span
              v-if="feedbackType === 'evidence' && note && note.version_label"
              :class="['version-label', { current: note.version_label === 'Current Version' }]"
            >
              {{ note.version_label }}
            </span>
            <span v-else :class="projectPhase">{{ projectPhase }}</span>
          </div>
        </template>
        </notes-overview>
      </div>
      </div>

    </template>
  </k-panel>
</template>

<style scoped>
.scoping,
.general,
.completed,
.evidence {
  color: var(--kate-type-dark);
}

.scoping {
  background-color: var(--kate-monitor);
}

[data-theme="light"] .scoping {
  background-color: var(--kate-type-warning);
}

.general {
  background-color: var(--kate-type-blue);
}

.completed {
  background-color: var(--kate-success);
}

.evidence {
  background-color: var(--kate-type-blue);
}

.scoping::before,
.general::before,
.completed::before,
.evidence::before {
  border-color: transparent var(--current-color) transparent transparent;
}

[data-theme="light"] .scoping::before { --current-color: var(--kate-type-warning); }
.scoping::before { --current-color: var(--kate-monitor); }
.general::before { --current-color: var(--kate-type-blue); }
.completed::before { --current-color: var(--kate-success); }
.evidence::before { --current-color: var(--kate-type-blue); }

.tags .version-label {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: transparent;
  border: 1px solid var(--kate-type-blue);
  color: var(--kate-type-blue);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.tags .version-label.current {
  background-color: var(--kate-success);
  border-color: var(--kate-success);
  color: var(--kate-type-dark);
}

/* Remove any decorative pseudo elements around the version pill */
.tags .version-label::before,
.tags .version-label::after {
  content: none !important;
  display: none !important;
  border: 0 !important;
}

/* Read-only evidence feedback customisations */
.read-only :deep(.note-actions) {
  display: none !important;
}

.read-only :deep(.note-details .author-info) {
  display: none !important;
}

/* Make notes list expand with the panel */
.read-only.evidence-mode :deep(.main-notes-container) {
  height: auto !important;
  max-height: none !important;
}

.read-only.evidence-mode :deep(.notes-overview) {
  height: auto !important;
}

/* Hide the left notch/rectangle from note container within this panel */
.read-only.evidence-mode :deep(.note-container::before) {
  display: none !important;
  content: none !important;
  border: 0 !important;
  border-width: 0 !important;
  border-right-color: transparent !important;
}

/* Override the fixed 300px height from notes-overview */
.feedback-layout :deep(.main-notes-container) {
  margin-top: 20px !important;
  height: 600px !important;
  overflow-y: auto;
}

.feedback-layout--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 24px 16px;
}

.feedback-layout--empty :deep(.empty-placeholder-content) {
  width: 100%;
}

.feedback-layout--empty :deep(.empty-placeholder-content i) {
  font-size: 4em;
}
</style>

<script>
import KPanel from '../../../../components/k-panel.vue';
import EmptyPlaceholder from '../../../../components/empty-placeholder.vue';
import NotesOverview from '../../../student/notes-overview.vue';

export default {
  name: 'ProjectReviewFeedback',
  components: {
    KPanel,
    EmptyPlaceholder,
    NotesOverview,
  },

  props: {
    projectData: {
      type: Object,
      required: true,
    },
    feedbackType: {
      type: String,
      required: true,
      validator: value => ['scope', 'evidence'].includes(value),
    },
    customTitle: {
      type: String,
      default: undefined,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    evidenceId: {
      type: Number,
      default: null,
    },
    versions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      feedbackNotes: [],
      feedbackLoaded: false,
    };
  },

  mounted() {
    this.getFeedback();
  },

  computed: {
    panelTitle() {
      if (this.customTitle) {
        return this.customTitle;
      }
      return this.feedbackType === 'scope' ? 'Scope Feedback' : 'Evidence Feedback';
    },

    projectPhase() {
      return this.feedbackType === 'scope' ? 'scoping' : 'evidence';
    },

    showScopeEmptyPlaceholder() {
      return this.feedbackType === 'scope' && this.feedbackLoaded && this.feedbackNotes.length === 0;
    },

    scopeEmptyPlaceholder() {
      if (this.projectData?.project_completed) {
        return {
          title: 'No scope feedback',
          info: 'No notes were made prior to completion.',
          faIcon: 'fas fa-clipboard-check',
        };
      }
      if (this.projectData?.scoping_completed) {
        return {
          title: 'No scope feedback',
          info: 'No notes were recorded before the scope was approved.',
          faIcon: 'fas fa-lock',
        };
      }
      return {
        title: 'No scope feedback yet',
        info: 'You can add feedback in the New Feedback panel.',
        faIcon: 'fas fa-comment-dots',
      };
    },
  },

  watch: {
    versions: {
      deep: true,
      handler() {
        // When evidence versions change (e.g., after saving new feedback), refresh the rendered notes
        if (this.feedbackType === 'evidence' && this.evidenceId) {
          this.getFeedback();
        }
      },
    },
  },

  methods: {
    getFeedback() {
      // If on evidence detail with versions provided, derive notes from versions
      if (this.feedbackType === 'evidence' && this.evidenceId && Array.isArray(this.versions) && this.versions.length) {
        const notes = this.formatEvidenceVersionsToNotes(this.versions);
        this.feedbackNotes = notes;
        this.feedbackLoaded = true;
        return Promise.resolve(notes);
      }

      // Fallback to project-level feedback fetch
      this.$logger.info('Fetching feedback', { projectId: this.projectData.id });
      this.feedbackLoaded = false;
      this.$Loading.start();

      return this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/feedback`)
        .then(response => {
          this.$logger.info('Successfully fetched feedback', { projectId: this.projectData.id });
          const feedbackData = response.data || [];
          return this.formatFeedbackToNotes(feedbackData);
        })
        .then(formattedFeedback => {
          const phaseFeedback = formattedFeedback.filter(feedback => feedback.project_phase === this.projectPhase);
          this.feedbackNotes = phaseFeedback;
        })
        .catch(error => {
          this.$logger.error('Error fetching feedback', { projectId: this.projectData.id }, error);
          this.$ktoast.showError(error, true);
          this.feedbackNotes = [];
        })
        .finally(() => {
          this.feedbackLoaded = true;
          this.$Loading.finish();
        });
    },

    editFeedback(feedbackId, feedbackData) {
      this.$logger.info('Editing feedback', { projectId: this.projectData.id, feedbackId });

      return this.$http.put(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/feedback`, {
        feedback_id: feedbackId,
        feedback_text: feedbackData,
        project_phase_at_feedback: this.projectPhase,
      })
        .then(response => {
          this.$logger.info('Successfully edited feedback', { projectId: this.projectData.id, feedbackId });
          this.$ktoast.success('Feedback updated successfully');
          this.getFeedback();
          return response;
        })
        .catch(error => {
          this.$logger.error('Error editing feedback', { projectId: this.projectData.id, feedbackId }, error);
          this.$ktoast.showError(error, true);
        });
    },

    deleteFeedback(feedbackId) {
      this.$logger.info('Deleting feedback', { projectId: this.projectData.id, feedbackId });

      return this.$http.delete(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/feedback/${feedbackId}`)
        .then(response => {
          this.$logger.info('Feedback deleted successfully', { projectId: this.projectData.id, feedbackId });
          this.$ktoast.success('Feedback deleted successfully');
          this.getFeedback();
          return response;
        })
        .catch(error => {
          this.$logger.error('Error deleting feedback', { projectId: this.projectData.id, feedbackId }, error);
          this.$ktoast.showError(error, true);
        });
    },

    formatFeedbackToNotes(feedback) {
      // If no feedback, return empty array
      if (!Array.isArray(feedback) || feedback.length === 0) {
        return [];
      }

      // Get unique user IDs and fetch their names
      const userIds = [...new Set(feedback.map(f => f.author_user_id))];
      return this.fetchUserNames(userIds)
        .then(authorNames => this.transformFeedbackToNotes(feedback, authorNames));
    },

    fetchUserNames(userIds) {
      const authorNames = {};
      const namePromises = userIds.map(userId => this.fetchUserName(userId)
        .then(name => {
          authorNames[userId] = name;
        }));

      return Promise.all(namePromises).then(() => authorNames);
    },

    fetchUserName(userId) {
      return this.$http.get(`/api/profile/users/${userId}`)
        .then(res => res.data.full_name)
        .catch(error => {
          this.$logger.warn('Failed to fetch user name', { userId }, error);
          return 'Mentor';
        });
    },

    transformFeedbackToNotes(feedback, authorNames) {
      return feedback.map(feedbackItem => ({
        note_id: feedbackItem.id,
        programme_member_id: null,
        note: feedbackItem.feedback_text,
        date_created: feedbackItem.created_at,
        date_updated: null,
        author_user_id: feedbackItem.author_user_id,
        author_name: authorNames[feedbackItem.author_user_id] || 'Mentor',
        share_with_employer: false,
        source: 'project_feedback',
        project_phase: feedbackItem.project_phase_at_feedback,
      }));
    },

    formatEvidenceVersionsToNotes(versions) {
      // Map evidence versions to notes; current version first, then previous by version_number desc
      // Only include entries with feedback_text
      const withText = versions.filter(v => Boolean(v && v.feedback_text && String(v.feedback_text).trim()))
        .slice();
      withText.sort((a, b) => {
        if (a.is_current_version) return -1;
        if (b.is_current_version) return 1;
        return (b.version_number || 0) - (a.version_number || 0);
      });
      return withText.map(v => ({
        note_id: v.version_id ?? `evidence-${this.evidenceId}-v-${v.version_number ?? 'unknown'}`,
        programme_member_id: null,
        note: v.feedback_text,
        date_created: null,
        date_updated: null,
        author_user_id: v.feedback_author_user_id || null,
        author_name: 'Mentor',
        share_with_employer: false,
        source: 'evidence_feedback',
        project_phase: 'evidence',
        version_label: v.is_current_version ? 'Current Version' : `v${v.version_number}`,
      }));
    },
  },
};
</script>

<style scoped>
.read-only :deep(.header-buttons) {
  display: none !important;
}

.read-only :deep(.note-editor) {
  display: none !important;
}
</style>
